import React, { useState, useEffect } from 'react';
import './AboutEditor.css';

function AboutEditor() {
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    mission: '',
    vision: '',
    values: '',
    story: '',
    team: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/about', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data) {
        setContent(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching about content:', error);
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/about/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      if (response.ok) {
        setMessage('Οι αλλαγές αποθηκεύτηκαν επιτυχώς! ✓');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Σφάλμα κατά την αποθήκευση');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage('Σφάλμα κατά την αποθήκευση');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Φόρτωση...</div>;
  }

  return (
    <div className="about-editor">
      <div className="editor-header">
        <h2>Επεξεργασία Σελίδας "Σχετικά με εμάς"</h2>
        <button 
          onClick={handleSave} 
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('επιτυχώς') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="editor-content">
        {/* Title & Subtitle */}
        <div className="editor-section">
          <h3>Τίτλος & Υπότιτλος</h3>
          <div className="form-group">
            <label>Τίτλος Σελίδας</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="π.χ. Σχετικά με το Freelancher"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Υπότιτλος</label>
            <input
              type="text"
              value={content.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              placeholder="π.χ. Η πλατφόρμα που συνδέει Έλληνες IT Freelancers"
              className="form-input"
            />
          </div>
        </div>

        {/* Mission */}
        <div className="editor-section">
          <h3>🎯 Η Αποστολή μας</h3>
          <div className="form-group">
            <label>Περιγράψτε την αποστολή της εταιρείας</label>
            <textarea
              value={content.mission}
              onChange={(e) => handleChange('mission', e.target.value)}
              placeholder="Περιγράψτε την αποστολή σας..."
              rows="6"
              className="form-textarea"
            />
            <small>Υποστηρίζονται HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</small>
          </div>
        </div>

        {/* Vision */}
        <div className="editor-section">
          <h3>🚀 Το Όραμά μας</h3>
          <div className="form-group">
            <label>Περιγράψτε το όραμά σας για το μέλλον</label>
            <textarea
              value={content.vision}
              onChange={(e) => handleChange('vision', e.target.value)}
              placeholder="Περιγράψτε το όραμά σας..."
              rows="6"
              className="form-textarea"
            />
            <small>Υποστηρίζονται HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</small>
          </div>
        </div>

        {/* Values */}
        <div className="editor-section">
          <h3>⭐ Οι Αξίες μας</h3>
          <div className="form-group">
            <label>Οι βασικές αξίες της εταιρείας</label>
            <textarea
              value={content.values}
              onChange={(e) => handleChange('values', e.target.value)}
              placeholder="Περιγράψτε τις αξίες σας..."
              rows="8"
              className="form-textarea"
            />
            <small>Προτείνεται να χρησιμοποιήσετε λίστα με &lt;ul&gt;&lt;li&gt;</small>
          </div>
        </div>

        {/* Story */}
        <div className="editor-section">
          <h3>📖 Η Ιστορία μας</h3>
          <div className="form-group">
            <label>Πείτε την ιστορία της εταιρείας</label>
            <textarea
              value={content.story}
              onChange={(e) => handleChange('story', e.target.value)}
              placeholder="Η ιστορία της εταιρείας σας..."
              rows="8"
              className="form-textarea"
            />
            <small>Διηγηθείτε πώς ξεκινήσατε, τις προκλήσεις, τις επιτυχίες...</small>
          </div>
        </div>

        {/* Team */}
        <div className="editor-section">
          <h3>👥 Η Ομάδα μας</h3>
          <div className="form-group">
            <label>Παρουσιάστε την ομάδα σας</label>
            <textarea
              value={content.team}
              onChange={(e) => handleChange('team', e.target.value)}
              placeholder="Περιγράψτε την ομάδα σας..."
              rows="8"
              className="form-textarea"
            />
            <small>Μιλήστε για τα μέλη της ομάδας, την εμπειρία, τις δεξιότητες...</small>
          </div>
        </div>

        {/* HTML Tips */}
        <div className="editor-tips">
          <h4>💡 Συμβουλές Μορφοποίησης</h4>
          <ul>
            <li><code>&lt;p&gt;Κείμενο&lt;/p&gt;</code> - Παράγραφος</li>
            <li><code>&lt;strong&gt;Κείμενο&lt;/strong&gt;</code> - Έντονη γραφή</li>
            <li><code>&lt;em&gt;Κείμενο&lt;/em&gt;</code> - Πλάγια γραφή</li>
            <li><code>&lt;ul&gt;&lt;li&gt;Στοιχείο&lt;/li&gt;&lt;/ul&gt;</code> - Λίστα με κουκκίδες</li>
            <li><code>&lt;ol&gt;&lt;li&gt;Στοιχείο&lt;/li&gt;&lt;/ol&gt;</code> - Αριθμημένη λίστα</li>
          </ul>
        </div>

        {/* Save Button Bottom */}
        <div className="editor-footer">
          <button 
            onClick={handleSave} 
            className="btn btn-primary btn-large"
            disabled={saving}
          >
            {saving ? 'Αποθήκευση...' : 'Αποθήκευση Όλων των Αλλαγών'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AboutEditor;
